import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import { decisionApiFor, type ISignedDecisionAct } from '@/services/DecisionApi';
import { noteAnswerTakenBack } from '@/features/decisions/nextWaitingDecision';
import {
  failureToSay,
  type HeldSentenceType,
  pageWords,
  sayFailure,
} from '@/services/actionFailure';
import { PresenceCancelledError } from '@/features/decisions/presenceCancelledError';

/**
 * IU-8 (F-0521, M-8 *Take it back*): the one toast an answer raises, held outside the decision
 * page's own component tree so it survives the navigation the answer itself causes — "the next
 * waiting decision slides in", and the toast's *Take it back* still works from the list a reader
 * goes back to afterwards (the concept's own Playwright case). A single active toast: a second
 * answer while one is showing replaces it, the same way a second Undo elsewhere in the concept
 * replaces rather than stacks.
 *
 * Mounted once (`DecisionAnswerToast`, in `Layout.tsx`) rather than inside `DecisionPanel`, which
 * unmounts the moment the reader leaves the item.
 */
export interface IDecisionAnswerToastState {
  readonly itemId: string;
  readonly itemKey: string;
  /** The decision's work area, so Undo reaches the deployment that holds it (I-1120). */
  readonly workAreaId?: string | null;
  /** What the toast names: the picked option's label, "Other", or however many were chosen. */
  readonly answerLabel: string;
  readonly expiresAt: number;
  /** F-0501 (AC-118): signs Undo's take-back on this device; absent, Undo goes unsigned. */
  readonly signTakeBack?: () => Promise<ISignedDecisionAct | null>;
}

/** M-8: the toast shows for this long before it clears itself. */
export const DECISION_ANSWER_TOAST_MS = 8_000;

/** A landed take-back, read once by whichever `DecisionPanel` is open on that same item. */
export interface IDecisionAnswerTakenBackSignal {
  readonly itemId: string;
  /** Distinguishes this landing from the last one the panel already consumed. */
  readonly at: number;
}

export class DecisionAnswerToastStore {
  toast: IDecisionAnswerToastState | null = null;
  /** Set once a take-back from the toast has been asked for and failed; cleared by a fresh show. */
  heldFailure: HeldSentenceType | null = null;
  get failure(): string | null {
    return sayFailure(pageWords(), this.heldFailure);
  }
  set failure(value: HeldSentenceType | null) {
    this.heldFailure = value;
  }
  /**
   * Slice 10c (AC-126): the person cancelled the passkey confirmation of the take-back, so nothing
   * was sent and the answer stands. The toast says so in the reader's language.
   */
  presenceCancelled = false;
  takingBack = false;
  /**
   * IU-8: a `DecisionPanel` still open on this item (the reader never left it) has no other way to
   * hear that its own answer was just taken back from this toast — `DecisionsStore.refresh()` below
   * updates the waiting badge and list, never a panel's own locally-held `decision`. Read by
   * `DecisionPanel`'s own effect, the same "consume once by `at`" idiom its `liveFollowSignal` uses.
   */
  takenBackSignal: IDecisionAnswerTakenBackSignal | null = null;
  /**
   * The decision whose next-step bar shows its own **Take it back** right now (F-0567 NX-1): one
   * visible take-back at a time, so the toast for that decision confirms the answer without its
   * own. The bar is pinned to the pane, so it is in view for as long as it is mounted.
   */
  takeBackInView: string | null = null;

  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeObservable(this, {
      toast: observable.ref,
      heldFailure: observable.ref,
      failure: computed,
      presenceCancelled: observable,
      takingBack: observable,
      takenBackSignal: observable.ref,
      takeBackInView: observable,
      offersTakeBack: computed,
      showTakeBackInView: action,
      hideTakeBackInView: action,
      show: action,
      dismiss: action,
      takenBackElsewhere: action,
      takeBack: action,
    });
  }

  show(
    itemId: string,
    itemKey: string,
    answerLabel: string,
    workAreaId?: string | null,
    signTakeBack?: () => Promise<ISignedDecisionAct | null>,
  ): void {
    if (this.timer) clearTimeout(this.timer);
    this.failure = null;
    this.presenceCancelled = false;
    this.toast = {
      itemId,
      itemKey,
      workAreaId,
      answerLabel,
      signTakeBack,
      expiresAt: Date.now() + DECISION_ANSWER_TOAST_MS,
    };
    this.timer = setTimeout(() => runInAction(() => this.dismiss()), DECISION_ANSWER_TOAST_MS);
  }

  dismiss(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.toast = null;
    this.failure = null;
    this.presenceCancelled = false;
  }

  /**
   * F-0567 (I-1604): the answer was taken back another way — the answered card's or the next-step
   * bar's own **Take it back** — so the toast that offers to take it back has nothing left to offer,
   * and goes. A toast for another decision stays.
   */
  /**
   * Whether the toast offers its own Take it back: not while the decision's next-step bar shows one.
   *
   * *Mutation:* always true → `DecisionAnswerToastStore.test.ts` › "offers Take it back only while
   * the next-step bar shows none" is red.
   */
  get offersTakeBack(): boolean {
    return this.toast !== null && this.toast.itemId !== this.takeBackInView;
  }

  showTakeBackInView(itemId: string): void {
    this.takeBackInView = itemId;
  }

  hideTakeBackInView(itemId: string): void {
    if (this.takeBackInView === itemId) this.takeBackInView = null;
  }

  takenBackElsewhere(itemId: string): void {
    if (this.toast?.itemId === itemId) this.dismiss();
  }

  /**
   * *A failed Undo leaves everything in place* (IU-8): a 500 here keeps the toast open with
   * **Retry**, rather than dismissing on failure the way a successful take-back does.
   */
  async takeBack(): Promise<void> {
    const current = this.toast;
    if (!current || this.takingBack) return;
    this.takingBack = true;
    this.failure = null;
    this.presenceCancelled = false;
    try {
      const signed = (await current.signTakeBack?.()) ?? null;
      await decisionApiFor(current.workAreaId).unanswer(current.itemId, signed);
      // F-0567 (NX-7): a run of answers moves past a decision taken back.
      noteAnswerTakenBack(current.itemId);
      runInAction(() => {
        this.takingBack = false;
        this.takenBackSignal = { itemId: current.itemId, at: Date.now() };
        this.dismiss();
      });
    } catch (error) {
      runInAction(() => {
        this.takingBack = false;
        if (error instanceof PresenceCancelledError) this.presenceCancelled = true;
        else this.failure = failureToSay(error, 'workItems.actions.takeBackAnswer');
      });
    }
  }

  /** Tests only. */
  resetForTests(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.toast = null;
    this.failure = null;
    this.presenceCancelled = false;
    this.takingBack = false;
    this.takenBackSignal = null;
    this.takeBackInView = null;
  }
}
